/**
 * Phone verification modal for loan application step navigation.
 * Expects window.phoneVerificationConfig from the Blade partial.
 */
const RESEND_COOLDOWN_SECONDS = 30;

export default class PhoneVerificationModal {
  constructor(config = {}) {
    this.config = Object.assign({
      sendUrl: '/loan-application/phone-verification/send',
      verifyUrl: '/loan-application/phone-verification/verify',
      resendUrl: '/loan-application/phone-verification/resend',
      contactUrl: '/contact-us',
      phoneFieldId: 'cell_phone',
      twoFactorEnabled: true,
      mobileValidationEnabled: false,
      invalidMobileMessage: 'Please enter a valid mobile phone number. This number is required to verify your application.',
    }, window.phoneVerificationConfig || {}, config);

    this.modal = document.getElementById('phoneVerificationModal');
    if (!this.modal) {
      return;
    }

    this.inputs = Array.from(this.modal.querySelectorAll('[data-phone-verification-digit]'));
    this.errorEl = document.getElementById('phoneVerificationModalError');
    this.statusEl = document.getElementById('phoneVerificationModalStatus');
    this.verifyBtn = this.modal.querySelector('[data-phone-verification-verify]');
    this.cancelBtn = this.modal.querySelector('[data-phone-verification-cancel]');
    this.resendBtn = this.modal.querySelector('[data-phone-verification-resend]');
    this.onSuccess = null;
    this.isSending = false;
    this.isVerifying = false;
    this.isResending = false;
    this.resendCooldownInterval = null;
    this.resendCooldownRemaining = 0;

    this.bindEvents();
  }

  isAvailable() {
    return !!this.modal;
  }

  isEnabled() {
    return this.config.twoFactorEnabled !== false;
  }

  isMobileValidationEnabled() {
    return this.config.mobileValidationEnabled === true;
  }

  stepRequiresVerification(step) {
    const stepContainers = {
      1: '#get_started_fields',
      2: '#employment_fields',
      3: '#banking_fields',
      4: '#disclosure_fields',
    };

    const container = document.querySelector(stepContainers[step]);
    if (!container) {
      return false;
    }

    return !!container.querySelector('#' + this.config.phoneFieldId);
  }

  open(onSuccess, triggerButton) {
    if (!this.modal) {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      return;
    }

    this.onSuccess = onSuccess;
    this.triggerButton = triggerButton || null;

    if (!this.isEnabled()) {
      this.continueWithoutVerification();
      return;
    }

    this.requestVerificationCode(false);
  }

  continueWithoutVerification() {
    const phone = this.getPhoneNumber();

    if (phone.length !== 10) {
      return;
    }

    if (this.isMobileValidationEnabled()) {
      this.setTriggerButtonLoading(true, 'Verifying...');
    }

    this.postForm(this.config.sendUrl, {
      cell_phone: phone,
    })
      .then((result) => {
        if (result.success !== true) {
          if (result.reason === 'invalid_mobile') {
            this.showPhoneFieldError(result.message || this.config.invalidMobileMessage);
          } else if (result.message) {
            this.showPhoneFieldError(result.message);
          }
          return;
        }

        const callback = this.onSuccess;
        this.onSuccess = null;

        if (typeof callback === 'function') {
          callback();
        }
      })
      .catch(() => {
        this.showPhoneFieldError('Unable to verify the phone number. Please try again.');
      })
      .finally(() => {
        if (this.isMobileValidationEnabled()) {
          this.setTriggerButtonLoading(false);
        }
      });
  }

  close() {
    if (!this.modal) {
      return;
    }

    this.stopResendCooldown();
    this.hide();
    this.onSuccess = null;
    this.clearError();
    this.clearStatus();
    this.clearInputs();
    this.setButtonsDisabled(false);
  }

  bindEvents() {
    this.cancelBtn.addEventListener('click', () => this.close());
    this.verifyBtn.addEventListener('click', () => this.handleVerify());

    if (this.resendBtn) {
      this.resendBtn.addEventListener('click', () => this.handleResend());
    }

    this.modal.querySelectorAll('[data-phone-verification-dismiss]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    this.inputs.forEach((input, index) => {
      input.addEventListener('input', (event) => this.handleInput(event, index));
      input.addEventListener('keydown', (event) => this.handleKeydown(event, index));
      input.addEventListener('paste', (event) => this.handlePaste(event));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        this.close();
      }
    });
  }

  handleInput(event, index) {
    const input = event.target;
    const digit = input.value.replace(/\D/g, '').slice(-1);
    input.value = digit;

    if (digit && index < this.inputs.length - 1) {
      this.inputs[index + 1].focus();
    }

    if (this.hasError()) {
      this.clearError();
    }

    this.maybeAutoVerify();
  }

  handleKeydown(event, index) {
    if (event.key !== 'Backspace') {
      return;
    }

    const input = this.inputs[index];

    if (!input.value && index > 0) {
      event.preventDefault();
      const previous = this.inputs[index - 1];
      previous.value = '';
      previous.focus();
    }
  }

  handlePaste(event) {
    event.preventDefault();
    const pasted = (event.clipboardData || window.clipboardData).getData('text');
    const digits = pasted.replace(/\D/g, '').slice(0, this.inputs.length);

    if (!digits.length) {
      return;
    }

    this.inputs.forEach((input, index) => {
      input.value = digits[index] || '';
    });

    const focusIndex = Math.min(digits.length, this.inputs.length) - 1;
    this.inputs[focusIndex].focus();

    if (this.hasError()) {
      this.clearError();
    }

    this.maybeAutoVerify();
  }

  maybeAutoVerify() {
    if (this.isVerifying || this.isSending || !this.isCodeComplete()) {
      return;
    }

    this.handleVerify();
  }

  isCodeComplete() {
    return this.getCode().length === this.inputs.length;
  }

  handleVerify() {
    if (this.isVerifying) {
      return;
    }
    const code = this.getCode();

    if (code.length !== this.inputs.length) {
      this.showVerifyError();
      return;
    }

    this.verifyCode(code);
  }

  getCode() {
    return this.inputs.map((input) => input.value.replace(/\D/g, '')).join('');
  }

  getPhoneField() {
    const stepContainer = document.querySelector('#get_started_fields');

    if (stepContainer) {
      const visibleField = stepContainer.querySelector('[name="cell_phone"]:not([type="hidden"])');
      if (visibleField) {
        return visibleField;
      }
    }

    return document.getElementById(this.config.phoneFieldId);
  }

  getPhoneNumber() {
    const field = this.getPhoneField();
    if (!field) {
      return '';
    }

    return String(field.value || '').replace(/\D/g, '');
  }

  getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) {
      return meta.getAttribute('content');
    }

    const tokenField = document.getElementById('_token');
    return tokenField ? tokenField.value : '';
  }

  sendVerificationCode() {
    this.requestVerificationCode(true);
  }

  requestVerificationCode(showModalOnSuccess) {
    const phone = this.getPhoneNumber();

    if (phone.length !== 10) {
      this.showPhoneFieldError('Please enter a valid 10-digit cell phone number before continuing.');
      return;
    }

    this.isSending = true;
    this.setButtonsDisabled(true);
    this.clearPhoneFieldError();

    if (!showModalOnSuccess && this.isMobileValidationEnabled()) {
      this.setTriggerButtonLoading(true, 'Verifying...');
    }

    if (showModalOnSuccess) {
      this.clearError();
      this.setStatus('Sending verification code...');
    }

    this.postForm(this.config.sendUrl, {
      cell_phone: phone,
    })
      .then((result) => {
        if (result.success === false) {
          if (result.reason === 'invalid_mobile') {
            this.showPhoneFieldError(result.message || this.config.invalidMobileMessage);
            if (showModalOnSuccess) {
              this.close();
            }
            return;
          }

          if (showModalOnSuccess) {
            this.showSendError(result.message);
          } else {
            this.showPhoneFieldError(result.message || 'Unable to send verification code. Please try again.');
          }
          return;
        }

        if (result.two_factor_enabled === false) {
          const callback = this.onSuccess;
          this.onSuccess = null;
          if (showModalOnSuccess) {
            this.close();
          }
          if (typeof callback === 'function') {
            callback();
          }
          return;
        }

        if (!showModalOnSuccess) {
          this.clearError();
          this.clearStatus();
          this.clearInputs();
          this.show();
          this.focusFirstInput();
          this.startResendCooldown(result.cooldown_seconds || RESEND_COOLDOWN_SECONDS);
        }

        this.clearStatus();
        this.setStatus('Verification code sent. Check your phone.');
      })
      .catch(() => {
        const message = 'Unable to send verification code. Please try again.';
        if (showModalOnSuccess) {
          this.showSendError(message);
        } else {
          this.showPhoneFieldError(message);
        }
      })
      .finally(() => {
        this.isSending = false;
        this.setButtonsDisabled(false);

        if (!showModalOnSuccess && this.isMobileValidationEnabled()) {
          this.setTriggerButtonLoading(false);
        }
      });
  }

  showPhoneFieldError(message) {
    const errorMessage = message || this.config.invalidMobileMessage;
    const field = this.getPhoneField();

    if (!field || field.type === 'hidden') {
      this.showPagePhoneError(errorMessage);
      return;
    }

    this.clearPagePhoneError();

    field.classList.add('is-invalid');

    let feedback = field.nextElementSibling;
    if (!feedback || !feedback.classList.contains('invalid-feedback')) {
      feedback = document.createElement('div');
      feedback.className = 'invalid-feedback';
      field.insertAdjacentElement('afterend', feedback);
    }

    feedback.textContent = errorMessage;
    field.focus();
  }

  clearPhoneFieldError() {
    this.clearPagePhoneError();

    const field = this.getPhoneField();
    if (!field) {
      return;
    }

    field.classList.remove('is-invalid');

    const feedback = field.nextElementSibling;
    if (feedback && feedback.classList.contains('invalid-feedback')) {
      feedback.remove();
    }
  }

  showPagePhoneError(message) {
    const pageError = document.getElementById('phoneVerificationPageError');
    if (!pageError) {
      return;
    }

    pageError.textContent = message;
    pageError.hidden = false;
    pageError.style.display = '';
  }

  clearPagePhoneError() {
    const pageError = document.getElementById('phoneVerificationPageError');
    if (!pageError) {
      return;
    }

    pageError.textContent = '';
    pageError.hidden = true;
    pageError.style.display = 'none';
  }

  handleResend() {
    if (!this.resendBtn || this.resendBtn.disabled || this.isResending) {
      return;
    }

    const phone = this.getPhoneNumber();

    if (phone.length !== 10) {
      return;
    }

    this.isResending = true;
    this.updateResendButton();
    this.clearError();

    this.postForm(this.config.resendUrl, {
      cell_phone: phone,
    })
      .then((result) => {
        if (result.success === false) {
          if (result.reason === 'cooldown') {
            this.startResendCooldown(result.retry_after || RESEND_COOLDOWN_SECONDS);
            return;
          }

          this.setStatus(result.message || 'Unable to resend verification code. Please try again.');
          this.updateResendButton();
          return;
        }

        this.clearInputs();
        this.focusFirstInput();
        this.clearError();
        this.setStatus(result.message || 'A new verification code has been sent.');
        this.startResendCooldown(result.cooldown_seconds || RESEND_COOLDOWN_SECONDS);
      })
      .catch(() => {
        this.setStatus('Unable to resend verification code. Please try again.');
        this.updateResendButton();
      })
      .finally(() => {
        this.isResending = false;
        this.updateResendButton();
      });
  }

  startResendCooldown(seconds) {
    this.stopResendCooldownInterval();
    this.resendCooldownRemaining = Math.max(0, parseInt(seconds, 10) || RESEND_COOLDOWN_SECONDS);
    this.updateResendButton();

    if (this.resendCooldownRemaining <= 0) {
      return;
    }

    this.resendCooldownInterval = setInterval(() => {
      this.resendCooldownRemaining -= 1;

      if (this.resendCooldownRemaining <= 0) {
        this.resendCooldownRemaining = 0;
        this.stopResendCooldownInterval();
      }

      this.updateResendButton();
    }, 1000);
  }

  stopResendCooldown() {
    this.stopResendCooldownInterval();
    this.resendCooldownRemaining = 0;
    this.updateResendButton();
  }

  stopResendCooldownInterval() {
    if (this.resendCooldownInterval) {
      clearInterval(this.resendCooldownInterval);
      this.resendCooldownInterval = null;
    }
  }

  updateResendButton() {
    if (!this.resendBtn) {
      return;
    }

    const disabled = this.resendCooldownRemaining > 0
      || this.isResending
      || this.isSending
      || this.isVerifying;

    this.resendBtn.disabled = disabled;

    if (this.resendCooldownRemaining > 0) {
      this.resendBtn.textContent = 'Resend Code (' + this.resendCooldownRemaining + ')';
      return;
    }

    this.resendBtn.textContent = 'Resend Code';
  }

  verifyCode(code) {
    const phone = this.getPhoneNumber();

    this.isVerifying = true;
    this.setButtonsDisabled(true);
    this.setStatus('Verifying code...');

    this.postForm(this.config.verifyUrl, {
      cell_phone: phone,
      verification_code: code,
    })
      .then((result) => {
        if (result.success === true) {
          const callback = this.onSuccess;
          this.close();
          if (typeof callback === 'function') {
            callback();
          }
          return;
        }

        this.showVerifyError();
        this.clearInputs();
        this.focusFirstInput();
      })
      .catch(() => {
        this.showVerifyError();
        this.clearInputs();
        this.focusFirstInput();
      })
      .finally(() => {
        this.isVerifying = false;
        this.setButtonsDisabled(false);
        this.clearStatus();
      });
  }

  postForm(url, data) {
    const token = this.getCsrfToken();
    const body = new URLSearchParams(Object.assign({ _token: token }, data));

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': token,
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: body.toString(),
    }).then((response) => this.parseResponse(response));
  }

  parseResponse(response) {
    const contentType = response.headers.get('content-type') || '';

    if (contentType.indexOf('application/json') !== -1) {
      return response.json().then((data) => {
        if (typeof data.success === 'boolean') {
          return data;
        }

        if (data.errors) {
          const firstError = Object.keys(data.errors)
            .map((key) => data.errors[key][0])
            .find(Boolean);

          return {
            success: false,
            message: firstError || data.message || 'Request failed.',
          };
        }

        return {
          success: response.ok,
          message: data.message || null,
        };
      });
    }

    return Promise.resolve({
      success: false,
      message: 'Unexpected response from server. Please try again.',
    });
  }

  showSendError(message) {
    this.clearStatus();
    this.setStatus(message || 'Unable to send verification code. Please try again.');
  }

  showVerifyError() {
    this.clearStatus();
    this.showError();
  }

  show() {
    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('phone-verification-modal-open');
  }

  hide() {
    this.modal.hidden = true;
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('phone-verification-modal-open');
  }

  isOpen() {
    return this.modal && !this.modal.hidden;
  }

  showError() {
    this.modal.classList.add('phone-verification-modal--error');
    this.errorEl.hidden = false;
  }

  clearError() {
    this.modal.classList.remove('phone-verification-modal--error');
    this.errorEl.hidden = true;
  }

  hasError() {
    return this.modal.classList.contains('phone-verification-modal--error');
  }

  setStatus(message) {
    if (!message) {
      this.clearStatus();
      return;
    }

    this.statusEl.textContent = message;
    this.statusEl.hidden = false;
  }

  clearStatus() {
    this.statusEl.textContent = '';
    this.statusEl.hidden = true;
  }

  clearInputs() {
    this.inputs.forEach((input) => {
      input.value = '';
    });
  }

  focusFirstInput() {
    if (this.inputs[0]) {
      this.inputs[0].focus();
    }
  }

  setButtonsDisabled(disabled) {
    const shouldDisable = disabled || this.isSending || this.isVerifying;
    this.verifyBtn.disabled = shouldDisable;
    this.cancelBtn.disabled = shouldDisable;
    this.updateResendButton();
  }

  setTriggerButtonLoading(isLoading, loadingText) {
    const button = this.triggerButton;

    if (!button) {
      return;
    }

    if (isLoading) {
      if (!button.dataset.phoneVerificationOriginalHtml) {
        button.dataset.phoneVerificationOriginalHtml = button.innerHTML;
      }

      button.disabled = true;
      button.classList.add('nextBtn--loading');
      button.setAttribute('aria-busy', 'true');
      button.innerHTML = '<span class="nextBtn__spinner" aria-hidden="true"></span> ' + (loadingText || 'Verifying...');
      return;
    }

    button.disabled = false;
    button.classList.remove('nextBtn--loading');
    button.removeAttribute('aria-busy');

    if (button.dataset.phoneVerificationOriginalHtml) {
      button.innerHTML = button.dataset.phoneVerificationOriginalHtml;
      delete button.dataset.phoneVerificationOriginalHtml;
    }
  }
}
